import { toast } from 'sonner';
import { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { useTheme } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  InputAdornment,
  CircularProgress,
  IconButton,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { useLogout } from 'src/hook/useLogout';
import { MOYASAR_PUBLIC_KEY } from 'src/config';
import { AuthLoader } from 'src/pages/auth/component/auth-loader';

import { Iconify } from 'src/components/iconify';

import { formatPrice } from '../../hook/formatPrice';
import { useProcessPaymentMutation } from '../../slice/settingApi';

interface CardDetailsDialogProps {
  open: boolean;
  onClose: (status?: any) => void;
  estimateItem: any;
  selectedPlan?: any;
  currency?: string;
  onSuccess?: (response: any) => void;
  cardDetailsKey?: string;
  actionPayload?: any;
  isSuperAdmin?: boolean;
}

interface CardFormValues {
  name: string;
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  cvc: string;
}

export default function CardDetailsDialog({
  open,
  onClose,
  estimateItem,
  selectedPlan,
  currency = 'SAR',
  onSuccess,
  cardDetailsKey,
  actionPayload,
  isSuperAdmin = false,
}: CardDetailsDialogProps) {
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [cardType, setCardType] = useState('');
  const [transactionUrl, setTransactionUrl] = useState<string>();
  const [paymentResult, setPaymentResult] = useState<{ id: string; status: string }>();
  const [paymentStatus, setPaymentStatus] = useState<any>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const { logoutHandler, isLoading } = useLogout();
  const { t } = useTranslate('translation');

  const { control, handleSubmit, formState, reset } = useForm<CardFormValues>({
    defaultValues: {
      name: '',
      cardNumber: '',
      expiryMonth: '',
      expiryYear: '',
      cvc: '',
    },
    mode: 'all',
  });

  const { errors } = formState;
  const [processPayment] = useProcessPaymentMutation();

  // ✅ Detect card type
  const detectCardType = (number: string) => {
    const cleanedNumber = number.replace(/\s+/g, '');
    if (/^4/.test(cleanedNumber)) return 'Visa';
    if (/^5[1-5]/.test(cleanedNumber)) return 'Mastercard';
    if (/^3[47]/.test(cleanedNumber)) return 'American Express';
    if (/^6(?:011|5)/.test(cleanedNumber)) return 'Discover';
    return '';
  };

  // ✅ Format card number
  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(' ') : value;
  };

  // ✅ Common helper to build addOns
  const buildAddOns = () =>
    estimateItem?.line_items.map((addon: any) => {
      let matched;

      if (Array.isArray(actionPayload?.addOnList)) {
        matched = actionPayload.addOnList.find((item: any) => item.id === addon.entity_id);
      }
      if (Array.isArray(actionPayload?.addOns)) {
        matched = actionPayload.addOns.find((item: any) => item.id === addon.entity_id);
      }
      if (actionPayload && typeof actionPayload === 'object') {
        if (actionPayload?.planId === addon?.entity_id) {
          matched = actionPayload;
        }
      }
      return {
        id: addon.entity_id,
        quantity: addon.quantity || 1,
        ...(matched?.couponId && { couponId: matched.couponId }),
      };
    }) || [];

  // ✅ Common helper to build payload
  const buildPayload = (extra: any = {}) => {
    const selectedPlanItem = estimateItem?.line_items?.find(
      (itm: any) => itm.entity_id === selectedPlan?.id || itm.entity_id === selectedPlan?.item_id
    );
    const planQuantity = selectedPlanItem?.quantity || 1;

    return {
      amount: estimateItem?.amount_due || estimateItem?.total,
      customer_id: estimateItem?.customer_id,
      ...(planQuantity && selectedPlan?.id && { planQuantity }),
      ...(selectedPlan?.id && { planId: selectedPlan.id }),
      ...(selectedPlan?.item_id && { packagesId: selectedPlan.item_id }),
      ...(cardDetailsKey && { key: cardDetailsKey }), // ✅ Always include cardDetailsKey
      ...(cardDetailsKey === 'invoice' && { invoiceId: estimateItem?.id || '' }), // ✅ Invoice case
      ...(cardDetailsKey === 'addBalance' && { items: [{ amount: actionPayload?.topUp || 0 }] }), // ✅ Add balance case
      ...(cardDetailsKey !== 'addBalance' && { items: buildAddOns() }), // ✅ Add balance case
      ...extra,
    };
  };

  const handleClose = () => {
    // const canClose =
    //   (!isSuperAdmin && ['idle', 'failed'].includes(paymentStatus)) ||
    //   (isSuperAdmin && ['idle', 'failed', 'success'].includes(paymentStatus));

    // if (!canClose) return;

    // common reset/cleanup

    if (['idle', 'failed', 'iframe'].includes(paymentStatus) && !isSuperAdmin) {
      toast.error(t('PAYMENT_FAILED_CANCELLED'));
    }

    setLoading(false);
    setCardType('');
    setTransactionUrl(undefined);
    setPaymentResult(undefined);
    setErrorMessage('');

    reset({
      name: '',
      cardNumber: '',
      expiryMonth: '',
      expiryYear: '',
      cvc: '',
    });

    onClose(paymentStatus);
    setPaymentStatus('idle');
  };

  // ✅ Main handler
  const handleTokenize = async (values: CardFormValues | 'confirmed') => {
    try {
      if (!estimateItem) return;
      setPaymentStatus('processing');
      setLoading(true);

      if (isSuperAdmin || estimateItem?.amount_due === 0) {
        // 🔹 Super Admin flow
        const payload = buildPayload({
          skipPayment: true,
          payload: actionPayload,
        });
        const response = await processPayment(payload).unwrap();
        if (response?.success || response?.status === 'success') {
          setPaymentStatus('success');
          toast.success(t('PAYMENT_SUCCESS'));
          setTimeout(() => {
            handleClose();
          }, 2000); // 2000 = 2 seconds
        } else {
          toast.error(t('PAYMENT_FAILED_RETRY'));
          setPaymentStatus('failed');
          setErrorMessage(response?.message || 'Unknown error');
        }
        return;
      }

      // 🔹 Tenant flow: Tokenize first
      const res = await fetch('https://api.moyasar.com/v1/tokens', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: (values as CardFormValues).name,
          number: (values as CardFormValues).cardNumber.replace(/\s+/g, ''),
          month: (values as CardFormValues).expiryMonth,
          year: (values as CardFormValues).expiryYear,
          cvc: (values as CardFormValues).cvc,
          publishable_api_key: MOYASAR_PUBLIC_KEY,
          callback_url: window.location.href,
          save_only: true,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data?.id) {
        throw new Error(data?.message || 'Failed to tokenize card');
      }

      const payload = buildPayload({
        token: data.id,
        payload: actionPayload,
        callbackUrl: `${window.location.origin}/callback`,
      });

      const response = await processPayment(payload).unwrap();
      setTransactionUrl(response?.data?.source?.transaction_url);
      setPaymentStatus('iframe');
    } catch (err: any) {
      console.error('❌ Payment failed:', err);
      const message = err?.data?.message || err.message || 'Payment failed';
      if (message === 'You have other unpaid invoices, please pay those first') {
        toast.error(t('UNPAID_INVOICES'));
        setErrorMessage(message);
      } else {
        toast.error(t('PAYMENT_FAILED_RETRY'));
      }
      setPaymentStatus('failed');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle iframe message
  const handleIframeMessage = (event: MessageEvent) => {
    const { id, status, message, type } = event.data;

    if (type === 'payment_result' && id && status) {
      setPaymentResult({ id, status });
      setTransactionUrl(undefined);

      if (status === 'paid' || status === 'success') {
        setPaymentStatus('success');
        if (onSuccess) onSuccess({ id, status });
      } else {
        setPaymentStatus('failed');
        setErrorMessage(message || t('PAYMENT_FAILED_RETRY'));
      }
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleIframeMessage);
    return () => window.removeEventListener('message', handleIframeMessage);
  }, []);

  // ✅ Copy text helper
  const copyText = (text: string) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  };

  const handleCopyTransactionId = () => {
    if (paymentResult?.id) {
      copyText(paymentResult.id);
      toast.success(t('TRANSACTION_ID_COPIED'));
    } else {
      toast.error(t('TRANSACTION_ID_NOT_AVAILABLE'));
    }
  };

  const handleTryAgain = () => {
    setPaymentStatus('idle');
    setTransactionUrl(undefined);
    setPaymentResult(undefined);
    setErrorMessage('');
    reset({
      name: '',
      cardNumber: '',
      expiryMonth: '',
      expiryYear: '',
      cvc: '',
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      disableAutoFocus
      PaperProps={{
        sx: {
          borderRadius: 3,
          height: 'auto',
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: theme.palette.background.paper,
        },
      }}
    >
      <Box
        sx={{
          p: 2,
          flexShrink: 0,
          ...((!isSuperAdmin || estimateItem?.amount_due === 0) && {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          }),
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center">
            {(!isSuperAdmin || estimateItem?.amount_due === 0) && (
              <Iconify icon="mdi:credit-card-outline" />
            )}
            <Typography variant="h6" sx={{ mx: 1 }}>
              {isSuperAdmin ? t('CONFIRM_PAYMENT') : t('SECURE_PAYMENT')}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              ...((!isSuperAdmin || estimateItem?.amount_due === 0) && {
                color: theme.palette.primary.contrastText,
              }),
            }}
          >
            <Iconify icon="mdi:close" width={24} height={24} />
          </IconButton>
        </Box>
        <Typography variant="body2" sx={{ mx: 1 }}>
          {isSuperAdmin || estimateItem?.amount_due === 0
            ? t('PAYMENT_PROCESSING_NOTE_SUPERADMIN')
            : t('PAYMENT_PROCESSING_NOTE')}
        </Typography>
      </Box>

      <DialogContent
        sx={{
          flex: 1,
          overflowY: 'auto', // 👈 allow scrolling
          display: 'flex',
          flexDirection: 'column',
          p: 1.5,
          pb: 0,
        }}
      >
        {loading && <AuthLoader />}

        {isSuperAdmin || estimateItem?.amount_due === 0 ? (
          // ✅ Super admin confirmation flow
          <>
            <Box sx={{ m: 2 }}>
              <Typography variant="h5" gutterBottom>
                {isSuperAdmin
                  ? t('BYPASS_PAYMENT_UNPAID_INVOICE')
                  : estimateItem?.amount_due === 0
                    ? t('NO_PAYMENT_NEEDED')
                    : t('PAYMENT_REQUIRED')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isSuperAdmin
                  ? t('CREATE_UNPAID_INVOICE_MSG', {
                      customer: estimateItem?.customer_name || 'this customer',
                      amount: formatPrice(estimateItem?.amount_due || estimateItem?.total),
                      currency,
                    })
                  : estimateItem?.amount_due === 0
                    ? t('INVOICE_COVERED_BY_CREDITS', {
                        customer: estimateItem?.customer_name || 'this customer',
                      })
                    : t('COMPLETE_PAYMENT_MSG', {
                        amount: formatPrice(estimateItem?.amount_due || estimateItem?.total),
                        currency,
                      })}
              </Typography>
            </Box>

            <DialogActions>
              <Box sx={{ display: 'flex', justifyContent: 'end', my: 2, gap: 2 }}>
                <Button onClick={handleClose} variant="outlined" color="inherit">
                  {t('CANCEL')}
                </Button>
                <Button
                  variant="contained"
                  color={isSuperAdmin ? 'success' : 'primary'}
                  onClick={() => handleTokenize('confirmed')}
                  sx={{ boxShadow: 'none' }}
                  aria-label={
                    isSuperAdmin ? t('BYPASS_PAYMENT_CREATE_UNPAID_INVOICE') : t('CREATE_INVOICE')
                  }
                >
                  {isSuperAdmin ? t('BYPASS_CREATE_UNPAID_INVOICE_SHORT') : t('CONFIRM')}
                </Button>
              </Box>
            </DialogActions>
          </>
        ) : (
          <>
            {(paymentStatus === 'idle' || paymentStatus === 'processing') && (
              <Box sx={{ p: 1, mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h3" fontWeight="600">
                    {formatPrice(estimateItem?.amount_due || estimateItem?.total)} {currency}
                  </Typography>
                  {cardType && (
                    <Box display="flex" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        {cardType}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('ENTER_CARD_DETAILS')}
                </Typography>
              </Box>
            )}

            {(paymentStatus === 'idle' || paymentStatus === 'processing') && (
              <Box sx={{ p: 1 }}>
                <form onSubmit={handleSubmit(handleTokenize)}>
                  <Grid container spacing={2}>
                    {/* Card Number */}
                    <Grid item xs={12}>
                      <Controller
                        name="cardNumber"
                        control={control}
                        rules={{
                          required: t('CARD_NUMBER_REQUIRED'),
                        }}
                        render={({ field: { onChange, value, ...field } }) => (
                          <TextField
                            fullWidth
                            label={t('CARD_NUMBER')}
                            placeholder="1234 5678 9012 3456"
                            {...field}
                            disabled={loading}
                            value={formatCardNumber(value)}
                            onChange={(e) => {
                              onChange(e);
                              setCardType(detectCardType(e.target.value));
                            }}
                            error={!!errors.cardNumber}
                            helperText={errors.cardNumber?.message}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Iconify icon="mdi:credit-card-outline" width={24} height={24} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* Cardholder Name */}
                    <Grid item xs={12}>
                      <Controller
                        name="name"
                        control={control}
                        rules={{ required: t('CARDHOLDER_NAME_REQUIRED') }}
                        render={({ field }) => (
                          <TextField
                            fullWidth
                            label={t('CARDHOLDER_NAME')}
                            placeholder="e.g. Mohammed Ali"
                            {...field}
                            error={!!errors.name}
                            disabled={loading}
                            helperText={errors.name?.message}
                            sx={{ background: 'transparent' }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Iconify icon="mdi:account-outline" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* Expiry Month & Year */}
                    <Grid item xs={6}>
                      <Controller
                        name="expiryMonth"
                        control={control}
                        rules={{
                          required: t('EXPIRY_MONTH_REQUIRED'),
                          pattern: {
                            value: /^(0[1-9]|1[0-2])$/,
                            message: t('EXPIRY_MONTH_INVALID'),
                          },
                        }}
                        render={({ field }) => (
                          <TextField
                            fullWidth
                            label={t('EXPIRY_MONTH')}
                            placeholder={t('EXPIRY_MONTH_PLACEHOLDER')}
                            {...field}
                            error={!!errors.expiryMonth}
                            disabled={loading}
                            helperText={errors.expiryMonth?.message}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Iconify icon="mdi:calendar-outline" width={24} height={24} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <Controller
                        name="expiryYear"
                        control={control}
                        rules={{
                          required: t('EXPIRY_YEAR_REQUIRED'),
                          pattern: {
                            value: /^\d{2,4}$/,
                            message: t('EXPIRY_YEAR_INVALID'),
                          },
                        }}
                        render={({ field }) => (
                          <TextField
                            fullWidth
                            disabled={loading}
                            label={t('EXPIRY_YEAR')}
                            placeholder={t('EXPIRY_YEAR_PLACEHOLDER')}
                            {...field}
                            error={!!errors.expiryYear}
                            helperText={errors.expiryYear?.message}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Iconify icon="mdi:calendar-outline" width={24} height={24} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* CVC */}
                    <Grid item xs={12}>
                      <Controller
                        name="cvc"
                        control={control}
                        rules={{
                          required: t('CVC_REQUIRED'),
                          pattern: {
                            value: /^\d{3,4}$/,
                            message: t('CVC_INVALID'),
                          },
                        }}
                        render={({ field }) => (
                          <TextField
                            fullWidth
                            label={t('CVC')}
                            placeholder="123"
                            {...field}
                            error={!!errors.cvc}
                            disabled={loading}
                            helperText={errors.cvc?.message}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Iconify icon="mdi:lock-outline" width={24} height={24} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  {/* Secure info */}
                  <Box mt={2} p={2} bgcolor={theme.palette.success.light} borderRadius={1}>
                    <Box display="flex" alignItems="center">
                      <Iconify icon="mdi:lock-outline" width={24} height={24} />
                      <Typography variant="caption" color={theme.palette.success.dark} ml={1}>
                        {t('CARD_DETAILS_ENCRYPTED')}
                      </Typography>
                    </Box>
                  </Box>

                  <DialogActions sx={{ px: 0, pt: 2 }}>
                    <Button
                      onClick={handleClose}
                      disabled={loading}
                      variant="outlined"
                      sx={{ borderRadius: 2 }}
                    >
                      {t('CANCEL')}
                    </Button>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled
                      sx={{ borderRadius: 2, minWidth: 140 }}
                      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                      {loading ? t('PROCESSING') : t('PAY')}
                    </Button>
                  </DialogActions>
                </form>
              </Box>
            )}

            {/* Success */}
            {paymentStatus === 'success' && (
              <Box
                textAlign="center"
                sx={{
                  p: 2,
                  height: '40vh',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h5" color={theme.palette.success.main}>
                  {t('PAYMENT_SUCCESSFUL')}
                </Typography>
                <Box
                  mt={2}
                  display="inline-flex"
                  alignItems="center"
                  px={1}
                  py={0.5}
                  bgcolor={theme.palette.action.hover}
                  borderRadius={1}
                >
                  <Typography variant="body1" sx={{ userSelect: 'all', mr: 1 }}>
                    {t('TRANSACTION_ID')} {paymentResult?.id}
                  </Typography>
                  <IconButton size="small" onClick={handleCopyTransactionId}>
                    <Iconify icon="mdi:content-copy" width={18} height={18} />
                  </IconButton>
                </Box>
                <Typography variant="body2" sx={{ my: 2 }}>
                  {t('PAYMENT_SUCCESSFUL_MSG')}
                </Typography>
                <DialogActions sx={{ alignSelf: 'flex-end' }}>
                  <Button onClick={logoutHandler} variant="contained" disabled={isLoading}>
                    {t('LOGOUT')}
                  </Button>
                </DialogActions>
              </Box>
            )}

            {/* Failure */}
            {paymentStatus === 'failed' && (
              <Box
                textAlign="center"
                sx={{
                  p: 2,
                  height: '40vh',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h5" color={theme.palette.error.main}>
                  {t('PAYMENT_FAILED')}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  {errorMessage || t('PAYMENT_FAILED_RETRY')}
                </Typography>
                <DialogActions sx={{ alignSelf: 'flex-end' }}>
                  <Button variant="contained" onClick={handleTryAgain}>
                    {t('TRY_AGAIN')}
                  </Button>
                  <Button variant="contained" onClick={handleClose}>
                    {t('CLOSE')}
                  </Button>
                </DialogActions>
              </Box>
            )}

            {/* Payment Iframe */}
            {paymentStatus === 'iframe' && transactionUrl && (
              <Box
                sx={{
                  height: '50vh',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                <iframe
                  ref={iframeRef}
                  src={transactionUrl}
                  title="Payment"
                  style={{
                    border: 'none',
                    width: '100%',
                    height: '100%',
                    borderRadius: 8,
                  }}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
